import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { CommonService } from '../../services/common.service';
import { LocalStorageService } from '../../services/local-storage.service';

import { AllDropdownKeys } from '../jurisdiction.constants';
import { JurisdictionService } from '../jurisdiction.service';
import { TechnicianDataService } from '../technician-data/technician-data.service';
import { GetGridConnectionObject, GridConnectionData } from '../technician-data/technician-data.domain';
import { ErrorService } from '../../services/error/error.service';
import { TechnicianFormType } from '../../constants/app.constants';
import { FacilitiesFormService } from '../facilities-form/facilities-form.service';
import { GetInstallationApplicationObject } from '../facilities-form/facilities-form.domain';
import { FormTypesData } from '../../domains/common.domain';
import { FileService } from '../../services/file.service';
import { HttpResponse } from '@angular/common/http';
import { CommunicationService } from '../../services/communication.service';
import { forkJoin } from 'rxjs';
import { TechnicianResponseData } from '../../employee-overview/employee-overview.domain';
import { EnvironmentService } from '../../services/environment.service';
import { NetzTranslateService } from '../../services/netz-translate.service';
import { PersonalDataComponent } from '../personal-data/personal-data.component';
import { FacilitiesDataComponent } from '../facilities-data/facilities-data.component';
import { TechnicalDataComponent } from '../technical-data/technical-data.component';

@Component({
  providers: [TechnicianDataService],
  selector: 'app-process-details',
  styleUrls: ['./process-details.component.scss'],
  templateUrl: './process-details.component.html',
})
export class ProcessDetailsComponent implements OnInit {
  @ViewChild(PersonalDataComponent) personalData: PersonalDataComponent;
  @ViewChild(FacilitiesDataComponent) facilitiesData: FacilitiesDataComponent;
  @ViewChild(TechnicalDataComponent) technicalData: TechnicalDataComponent;

  applicationId: string;
  facilityTypes: FormTypesData[];
  installationType: string;
  isLoading: boolean;
  isReadOnly: boolean;
  loadGridConnectionComponents: boolean;
  loadInstallationApplicationComponents: boolean;
  showSendMessageButton: boolean;
  showTechnicalFormFirst: boolean;
  showOrderProcessChangeBtns: boolean;
  showRequestChangeBtn: boolean;
  showUpdateApplicationBtn: boolean;
  executorAction: string;
  role: string;

  private applicationType: string;

  get dataIsEditable(): boolean {
    return (
      this.showRequestChangeBtn &&
      this.executorAction === 'update_application' &&
      (this.role === 'standard_technician' || this.role === 'guest_technician')
    );
  }

  constructor(
    private commonService: CommonService,
    private errorService: ErrorService,
    private facilitiesFormService: FacilitiesFormService,
    private fileService: FileService,
    private jurisdictionService: JurisdictionService,
    private localStorageService: LocalStorageService,
    private technicianDataService: TechnicianDataService,
    private route: ActivatedRoute,
    private router: Router,
    private communicationService: CommunicationService,
    private environmentService: EnvironmentService,
    private netzTranslateService: NetzTranslateService
  ) {}

  ngOnInit(): void {
    this.isLoading = false;
    this.applicationType = this.localStorageService.getItem('application_type');
    this.role = this.localStorageService.getItem('role');
    this.loadGridConnectionComponents = false;
    this.loadInstallationApplicationComponents = false;
    this.installationType = '';
    this.facilityTypes = this.localStorageService.getItem('selected_facilities') || [];
    this.isReadOnly = this.route.snapshot.queryParamMap.get('isReadOnly') === 'true';
    this.showTechnicalFormFirst = this.environmentService.showTechnicalFormFirst;
    this.showOrderProcessChangeBtns = this.environmentService.showOrderProcessChangeBtns;
    this.showRequestChangeBtn = this.environmentService.showRequestChangeBtn;

    if (this.applicationType) {
      this.getApplicationData();
    }
  }

  sendMessage(): void {
    const applicationTypes = this.commonService.getDropdownData(AllDropdownKeys.ApplicationType);
    let technicianId: string;
    let applicationData = null;

    if (this.applicationType === applicationTypes[1].value) {
      // connection-flow
      technicianId = this.commonService.gridConnectionApplicationData.user_id;
      applicationData = { ...this.commonService.gridConnectionApplicationData };
    } else if (this.applicationType === applicationTypes[0].value) {
      // installation-flow
      technicianId = this.commonService.installationApplicationData.technician_id;
      applicationData = { ...this.commonService.installationApplicationData };
    }

    const dataObj = {
      applicationType: this.localStorageService.getItem('application_type_detail'),
      externalId: applicationData.application_ext_id,
      connectionLocation: `${applicationData.connectionobject.postcode} ${applicationData.connectionobject.city}, ${applicationData.connectionobject.street} ${applicationData.connectionobject.street_no}`,
      businessPartner: `${applicationData.businesspartner.lastname}, ${applicationData.businesspartner.firstname}`,
    };
    const subjectArray = [`${this.netzTranslateService.translateString('messages.subject-text')}: ${dataObj.externalId}`];
    subjectArray.push(
      this.netzTranslateService.translateString(`lookup.sk_application_type_detail.options.${dataObj.applicationType}.name`)
    );
    subjectArray.push(...[dataObj.connectionLocation, dataObj.businessPartner]);
    const messageData = {
      subject: subjectArray.join(' | '),
      firstName: applicationData.businesspartner.firstname,
      lastName: applicationData.businesspartner.lastname,
    };
    this.localStorageService.setItem('message_data', messageData);

    this.router.navigate(['/jurisdiction-overview/messages', technicianId]);
  }

  print(): void {
    window.print();
  }

  downloadAllDocuments(): void {
    this.fileService.getAllDocumentsByApplicationId(this.applicationId).subscribe(
      (response: HttpResponse<Blob>) => {
        this.errorService.throwErrorIfFailure(response, false);
        this.fileService.downloadZipDocument(response);
      },
      error => {
        this.errorService.throwErrorIfFailure(error, true);
      }
    );
  }

  updateApplicationData(): void {
    this.personalData.saveFormData();

    if (this.facilitiesData) {
      this.facilitiesData.submitForm();
    }
    if (this.technicalData) {
      this.technicalData.saveFormData();
    }

    setTimeout(() => {
      this.errorService.createMultipleMessages([this.netzTranslateService.translateString('submitted.new_changes')], 'success');
    }, 1500);
  }

  setExecutorAction(executorAction: string): void {
    this.executorAction = executorAction;
  }

  private getApplicationData(): void {
    const applicationTypes = this.commonService.getDropdownData(AllDropdownKeys.ApplicationType);
    if (this.applicationType === applicationTypes[1].value) {
      // connection-flow
      this.getData();
    } else if (this.applicationType === applicationTypes[0].value) {
      // installation-flow
      this.isLoading = true;
      this.facilitiesFormService.getInstallationApplication().subscribe(
        (response: GetInstallationApplicationObject) => {
          this.isLoading = false;
          this.errorService.throwErrorIfFailure(response, true);
          this.commonService.installationApplicationData = response.installationapplication[0];
          this.installationType = response.installationapplication[0].sk_installation_type;
          this.applicationId = response.installationapplication[0].application_id;
          this.loadInstallationApplicationComponents = true;
          this.setMessageButtonVisibility();
        },
        error => {
          this.isLoading = false;
          this.errorService.throwErrorIfFailure(error, true);
        }
      );
    }
  }

  private getData(): void {
    const selectedTechnicianId = this.localStorageService.getItem('selected_technician_id');
    const applicationDataSubscription = this.technicianDataService.getGridConnectionApplication();
    const technicianDataSubscription = this.communicationService.getData(`technician?technician_id=${selectedTechnicianId}`);
    this.isLoading = true;
    forkJoin([applicationDataSubscription, technicianDataSubscription]).subscribe(
      (response: [GetGridConnectionObject, TechnicianResponseData]) => {
        this.isLoading = false;
        this.errorService.throwErrorIfFailure(response, true);
        if (response && response.length === 2) {
          this.commonService.gridConnectionApplicationData = response[0].gridconapplication[0];
          this.applicationId = response[0].gridconapplication[0].application_id;
          this.parseGridConnectionResponse(response[0].gridconapplication[0]);

          const technicianData = (response[1].technician && response[1].technician[0]) || null;
          if (technicianData) {
            this.commonService.getPermittedTradesData(technicianData);
          }
        }
      },
      error => {
        this.isLoading = false;
        this.errorService.throwErrorIfFailure(error, true);
      }
    );
  }

  private parseGridConnectionResponse(response: GridConnectionData): void {
    const selectedBuildingType = TechnicianFormType.filter(item => item.lookUpKey === response.sk_building_type);
    this.localStorageService.setItem('selected_building_type', selectedBuildingType);
    this.localStorageService.setItem('selected_change_type', response.sk_change_subtype);
    this.loadGridConnectionComponents = true;
    this.setMessageButtonVisibility();
  }

  private setMessageButtonVisibility(): void {
    this.showSendMessageButton =
      ['administrator', 'employee', 'observer'].indexOf(this.role) > -1 &&
      (this.loadInstallationApplicationComponents || this.loadGridConnectionComponents);
  }
}
